import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Pause, Play, Copy, Trash2, Search, ChevronDown, Workflow, Rocket, Edit, MoreHorizontal, Users, FileText, GitBranch, Calendar, AlertTriangle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import {
  useWorkflowsQuery,
  usePauseWorkflowMutation,
  useActivateWorkflowMutation,
  useDuplicateWorkflowMutation,
  useDeleteWorkflowMutation,
  getWorkflowSendingStatus,
  type Workflow as WorkflowType,
} from '@/hooks/useWorkflows';

const sendingStatusBadge = (status: ReturnType<typeof getWorkflowSendingStatus>) => {
  switch (status) {
    case 'pending': return { cls: 'wf-badge wf-badge-send-pending', label: 'Pending', pulse: false };
    case 'sending': return { cls: 'wf-badge wf-badge-send-sending', label: 'Sending', pulse: true };
    case 'failed':  return { cls: 'wf-badge wf-badge-send-failed',  label: 'Failed',  pulse: false };
    case 'done':    return { cls: 'wf-badge wf-badge-send-done',    label: 'Done',    pulse: false };
    default:        return null;
  }
};

const tabs = ['All', 'Active', 'Paused', 'Draft', 'Completed'];
const sortOptions = ['Last Modified', 'Name A-Z', 'Name Z-A', 'Date Created'];

const statusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':    return 'wf-badge-green';
    case 'paused':    return 'wf-badge-amber';
    case 'draft':     return 'wf-badge-gray';
    case 'completed': return 'wf-badge-completed';
    default:          return 'wf-badge-gray';
  }
};

const mapSortBy = (sortBy: string): string => {
  switch (sortBy) {
    case 'Last Modified': return 'updated_desc';
    case 'Name A-Z':      return 'name_asc';
    case 'Name Z-A':      return 'name_desc';
    case 'Date Created':  return 'created_asc';
    default:              return 'updated_desc';
  }
};

export default function WorkflowsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]             = useState('Active');
  const [searchQuery, setSearchQuery]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy]                   = useState('Last Modified');
  const [deleteTarget, setDeleteTarget]       = useState<WorkflowType | null>(null);
  const [openMenuId, setOpenMenuId]           = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data, isLoading } = useWorkflowsQuery({
    status:  activeTab === 'All' ? undefined : activeTab.toLowerCase(),
    search:  debouncedSearch || undefined,
    sort_by: mapSortBy(sortBy),
  });

  const pauseMutation     = usePauseWorkflowMutation();
  const activateMutation  = useActivateWorkflowMutation();
  const duplicateMutation = useDuplicateWorkflowMutation();
  const deleteMutation    = useDeleteWorkflowMutation();

  const workflows = data?.data?.workflows ?? [];
  const counts    = data?.data?.counts;

  const tabCount = (tab: string): number => {
    const map: Record<string, number> = {
      Active: counts?.active ?? 0, Paused: counts?.paused ?? 0,
      Draft: counts?.draft ?? 0, Completed: counts?.completed ?? 0,
    };
    return map[tab] ?? 0;
  };

  const handlePause = (wf: WorkflowType) =>
    pauseMutation.mutate(wf.id, {
      onSuccess: () => toast.success('Workflow paused', { description: `"${wf.title}" has been paused.` }),
      onError:   () => toast.error('Failed to pause workflow'),
    });

  const handleResume = (wf: WorkflowType) =>
    activateMutation.mutate(wf.id, {
      onSuccess: () => toast.success('Workflow resumed', { description: `"${wf.title}" is now active.` }),
      onError:   (err: unknown) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        toast.error('Failed to resume workflow', { description: msg });
      },
    });

  const handleDuplicate = (wf: WorkflowType) =>
    duplicateMutation.mutate(wf.id, {
      onSuccess: () => toast.success('Workflow duplicated', { description: `A copy of "${wf.title}" has been created.` }),
      onError:   () => toast.error('Failed to duplicate workflow'),
    });

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Workflow deleted', { description: `"${deleteTarget.title}" has been deleted.` });
        setDeleteTarget(null);
      },
      onError: () => {
        toast.error('Failed to delete workflow');
        setDeleteTarget(null);
      },
    });
  };

  const statusLabel = (status: string): string => status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <div className="portal-page wf-page">
      <div className="wf-header">
        <div>
          <h1 className="wf-title">Workflows</h1>
          <p className="wf-desc">Manage your automated outreach workflows</p>
        </div>
        <div className="wf-header-actions">
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/app/workflows/new')}>
            <Plus size={16} /> Create Workflow
          </button>
        </div>
      </div>

      <div className="wf-toolbar">
        <div className="wf-tabs">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`wf-tab ${activeTab === tab ? 'wf-tab--active' : ''}`}
            >
              {tab}
              {tab !== 'All' && <span className="wf-tab-count">{tabCount(tab)}</span>}
            </button>
          ))}
        </div>
        <div className="wf-toolbar-right">
          <div className="wf-search-wrap">
            <Search size={16} className="wf-search-icon" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="wf-search-input"
            />
          </div>
          <div className="wf-sort-wrap">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="wf-sort-select"
            >
              {sortOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown size={14} className="wf-sort-chevron" />
          </div>
        </div>
      </div>

      <div className="wf-list">
        {/* Loading skeletons */}
        {isLoading && [1, 2, 3].map(i => (
          <div key={i} className="card wf-card wf-inline-skeleton-card">
            <div className="wf-card-header">
              <div className="wf-card-info">
                <div className="wf-card-name-row">
                  <div className="wf-inline-skeleton-badge" />
                  <div className="wf-inline-skeleton-title" />
                </div>
                <div className="wf-inline-skeleton-desc" />
              </div>
            </div>
            <div className="wf-metrics">
              {[1,2,3,4].map(j => (
                <div key={j} className="wf-metric-item">
                  <div className="wf-inline-skeleton-metric" />
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty state */}
        {!isLoading && workflows.length === 0 && (
          <div className="wf-empty-state">
            <Workflow size={40} color="var(--gray-300)" />
            <p className="wf-inline-empty-title">No workflows found</p>
            <p className="wf-inline-empty-subtitle">Create your first workflow to get started.</p>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/app/workflows/new')}>
              <Plus size={14} /> Create your first workflow
            </button>
          </div>
        )}

        {/* Workflow cards */}
        {!isLoading && workflows.map((wf: WorkflowType) => {
          const sendingStatus   = getWorkflowSendingStatus(wf);
          const sendingBadge    = sendingStatusBadge(sendingStatus);
          const canEdit         = sendingStatus === null || sendingStatus === 'pending';
          const noSenderProfile = wf.campaign !== null && wf.campaign.sender_profile === null;
          return (
          <div key={wf.id} className={`card wf-card${noSenderProfile ? ' wf-card--no-sender' : ''}`}>
            <div className="wf-card-header">
              <div className="wf-card-info">
                <div className="wf-card-name-row">
                  <span className={`wf-badge ${statusBadgeClass(wf.status)}`}>
                    {wf.status === 'active' && <span className="wf-pulse-dot" />}
                    {statusLabel(wf.status)}
                  </span>
                  {sendingBadge && (
                    <span className={sendingBadge.cls}>
                      {sendingBadge.pulse && <span className="wf-pulse-dot" />}
                      {sendingBadge.label}
                    </span>
                  )}
                  {noSenderProfile && (
                    <span className="wf-credits-warn" data-tooltip="This campaign has no sender profile attached. Emails cannot be sent until a sender profile is configured.">
                      <AlertTriangle size={16} />
                    </span>
                  )}
                  {wf.credits_exhausted && (
                    <span
                      className="wf-credits-warn"
                      data-tooltip="Your workflow email sending has been stopped because your email credits were exhausted during the process. Please upgrade your plan and create a new workflow to send the remaining emails."
                    >
                      <AlertTriangle size={16} />
                    </span>
                  )}
                  {canEdit ? (
                    <Link to={`/app/workflows/${wf.id}/edit`} className="wf-card-link">
                      <h3 className="wf-card-title">{wf.title}</h3>
                    </Link>
                  ) : (
                    <span className="wf-card-link wf-card-link--disabled">
                      <h3 className="wf-card-title">{wf.title}</h3>
                    </span>
                  )}
                </div>
                <div className="wf-card-subtitle">
                  <Users size={14} />
                  <span>
                    {wf.segment?.name ?? '—'}
                    {wf.campaign ? ` · ${wf.campaign.name}` : ''}
                    {' · Created '}
                    {new Date(wf.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              </div>

              <div className="wf-card-actions">
                {wf.status === 'draft' && (
                  <button className="btn btn-primary btn-sm" onClick={() => navigate(`/app/workflows/${wf.id}/edit`)}>
                    <Rocket size={14} /> Launch
                  </button>
                )}
                {wf.status === 'active' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePause(wf)} disabled={pauseMutation.isPending}>
                    <Pause size={14} /> Pause
                  </button>
                )}
                {wf.status === 'paused' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleResume(wf)} disabled={activateMutation.isPending}>
                    <Play size={14} /> Resume
                  </button>
                )}
                {canEdit && (
                  <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/app/workflows/${wf.id}/edit`)}>
                    <Edit size={14} /> Edit
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => handleDuplicate(wf)} disabled={duplicateMutation.isPending}>
                  <Copy size={14} /> Duplicate
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/app/workflows/${wf.id}/emails`)}>
                  <Mail size={14} /> Email
                </button>
                {/* Overflow menu */}
                <div className="wf-inline-menu-anchor" ref={openMenuId === wf.id ? menuRef : null}>
                  <button
                    className="btn btn-secondary btn-sm wf-more-btn"
                    onClick={() => setOpenMenuId(openMenuId === wf.id ? null : wf.id)}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                  {openMenuId === wf.id && (
                    <div className="wf-overflow-menu">
                      <button
                        className="wf-overflow-item wf-overflow-item--danger"
                        onClick={() => { setOpenMenuId(null); setDeleteTarget(wf); }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="wf-metrics">
              {[
                { label: 'Leads',    value: wf.estimated_leads_count,      icon: Users,      color: 'var(--sky)'   },
                { label: 'Campaign', value: wf.campaign?.name     ?? '—', icon: FileText,    color: 'var(--amber)' },
                { label: 'Sequence', value: wf.sequence?.name     ?? '—', icon: GitBranch,   color: 'var(--green)' },
                { label: 'Created',  value: new Date(wf.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), icon: Calendar, color: 'var(--navy)' },
              ].map(m => (
                <div key={m.label} className="wf-metric-item">
                  <m.icon size={16} color={m.color} className="wf-inline-metric-icon" />
                  <p className="wf-metric-label">{m.label}</p>
                  <p className="wf-metric-value">{typeof m.value === 'number' ? m.value.toLocaleString() : m.value}</p>
                </div>
              ))}
            </div>
          </div>
          );
        })}
      </div>

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="wf-inline-dialog-overlay">
          <div className="wf-inline-dialog-box">
            <h3 className="wf-inline-dialog-title">Delete Workflow</h3>
            <p className="wf-inline-dialog-body">
              Are you sure you want to delete <strong>{deleteTarget.title}</strong>? This action cannot be undone.
            </p>
            <div className="wf-inline-dialog-actions">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary wf-inline-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
